import { QdzElement, adoptSectionStyles, applyCardOverrides, cardSection, define, parseHTML } from '@qdz/element';
import { lists } from '@qdz/lists';

const root = () => window.Shopify?.routes?.root ?? '/';
const cache = new Map();

async function fetchSection(handle, section, selector) {
  const key = `${section}:${handle}`;
  if (!cache.has(key)) {
    cache.set(key, (async () => {
      try {
        const response = await fetch(`${root()}products/${encodeURIComponent(handle)}?section_id=${section}`);
        if (!response.ok) return null;
        const doc = parseHTML(await response.text());
        adoptSectionStyles(doc);
        return doc.querySelector(selector);
      } catch {
        return null;
      }
    })());
  }
  const node = await cache.get(key);
  return node ? node.cloneNode(true) : null;
}

const sharedHandles = () => {
  const items = new URL(window.location.href).searchParams.get('items');
  return items ? [...new Set(items.split(',').map((handle) => handle.trim()).filter((handle) => /^[\w-]+$/.test(handle)))] : null;
};

export class QdzWishlist extends QdzElement {
  #shared = sharedHandles();
  #token = 0;

  get handles() {
    return this.#shared ?? lists.get('wishlist');
  }

  mount() {
    this.listen(document, 'qdz:lists:change', (event) => {
      if (event.detail.name === 'wishlist' && !this.#shared) this.render();
    });
    this.listen(this.ref('share'), 'click', () => this.#share());
    this.listen(this.ref('save-all'), 'click', () => {
      lists.set('wishlist', [...lists.get('wishlist'), ...this.#shared]);
      window.location.assign(window.location.pathname);
    });
    const compare = this.ref('compare');
    if (compare) {
      this.listen(compare, 'click', () => lists.set('compare', this.handles));
    }
    this.render();
  }

  #label(count) {
    return (count === 1 ? this.dataset.one : this.dataset.other).replace('[count]', count);
  }

  #announce(message) {
    this.ref('status').textContent = message;
  }

  async #share() {
    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('items', this.handles.join(','));
    try {
      await navigator.clipboard.writeText(url.href);
      this.ref('share').textContent = this.dataset.copied;
      this.#announce(this.dataset.copied);
    } catch {
      this.#announce(url.href);
    }
  }

  async render() {
    const token = ++this.#token;
    const handles = this.handles;
    this.ref('count').textContent = this.#shared ? this.dataset.shared.replace('[count]', handles.length) : this.#label(handles.length);
    this.ref('actions').hidden = handles.length === 0;
    this.ref('share').hidden = Boolean(this.#shared);
    this.ref('save-all').hidden = !this.#shared;
    const grid = this.ref('grid');
    const current = new Map([...grid.children].map((item) => [item.dataset.handle, item]));
    const cards = await Promise.all(handles.map(async (handle) => {
      if (current.has(handle)) return current.get(handle);
      const card = await fetchSection(handle, cardSection(this), '[data-qdz-card]');
      if (!card) return null;
      const item = document.createElement('li');
      item.dataset.handle = handle;
      item.append(...card.childNodes);
      applyCardOverrides(item, this);
      return item;
    }));
    if (token !== this.#token) return;
    const items = cards.filter(Boolean);
    grid.replaceChildren(...items);
    this.ref('empty').hidden = items.length > 0;
  }
}

export class QdzCompareTable extends QdzElement {
  #token = 0;
  #visible = false;
  #observer = null;

  mount() {
    this.listen(document, 'qdz:lists:change', (event) => {
      if (event.detail.name === 'compare' && this.#visible) this.render();
    });
    this.listen(this.ref('diff'), 'change', () => this.render());
    this.listen(this, 'click', (event) => {
      const remove = event.target.closest('[data-remove-compare]');
      if (remove) lists.toggle('compare', remove.dataset.removeCompare);
    });
    this.#observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.#visible = true;
      this.#observer.disconnect();
      this.render();
    });
    this.#observer.observe(this);
  }

  unmount() {
    this.#observer?.disconnect();
  }

  async render() {
    const token = ++this.#token;
    const handles = lists.get('compare');
    const columns = (await Promise.all(handles.map((handle) => fetchSection(handle, 'qdz-compare-column', '[data-qdz-compare-column]')))).filter(Boolean);
    if (token !== this.#token) return;
    this.ref('empty').hidden = columns.length > 0;
    this.ref('scroll').hidden = columns.length === 0;
    this.ref('diff').closest('label').hidden = columns.length < 2;
    if (!columns.length) {
      this.ref('status').textContent = '';
      return;
    }
    const rows = [...columns[0].querySelectorAll('[data-row]')].map((cell) => ({
      key: cell.dataset.row,
      label: cell.dataset.label,
      values: columns.map((column) => column.querySelector(`[data-row="${CSS.escape(cell.dataset.row)}"]`)?.textContent.replace(/\s+/g, ' ').trim() || '—'),
    }));
    const differencesOnly = this.ref('diff').checked;
    const visible = rows
      .filter((row) => row.values.some((value) => value !== '—'))
      .filter((row) => !differencesOnly || row.values.some((value) => value !== row.values[0]));

    const headRow = document.createElement('tr');
    const corner = document.createElement('th');
    corner.scope = 'col';
    corner.textContent = this.dataset.count.replace('[count]', columns.length);
    headRow.append(corner);
    for (const column of columns) {
      const th = document.createElement('th');
      th.scope = 'col';
      th.append(...column.querySelector('[data-cell="head"]').childNodes);
      headRow.append(th);
    }
    this.ref('head').replaceChildren(headRow);
    this.ref('body').replaceChildren(...visible.map((row) => {
      const tr = document.createElement('tr');
      const th = document.createElement('th');
      th.scope = 'row';
      th.textContent = row.label;
      tr.append(th, ...row.values.map((value) => Object.assign(document.createElement('td'), { textContent: value })));
      return tr;
    }));
    this.ref('status').textContent = corner.textContent;
  }
}

define('qdz-wishlist', QdzWishlist);
define('qdz-compare-table', QdzCompareTable);
